import React, { useState, useEffect, useCallback } from "react";
import {
  listDepartmentMembers,
  extractMemberEmails,
  assignUserToDepartment,
  removeUserFromDepartment,
} from "./departmentsApi";
import { normalizeApiError, formatErrorMessage } from "../../utils/apiErrors";
import "./AccessManagerPanel.css";

/**
 * Email validation regex — matches the one used by AddUserDialog.
 * HTML5-style: local@domain.tld
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * MembersView — expandable inline panel listing a department's members
 * with add and remove controls.
 *
 * Rendered as a full-width `<td colSpan={5}>` inside a `<tr>` beneath the
 * department row. Fetches the member list on mount (i.e. on expand) and
 * supports optimistic add/remove with count callbacks to the parent.
 *
 * @param {Object} props
 * @param {{ id: number, name: string }} props.department
 * @param {(delta: 1|-1) => void} props.onMemberCountDelta - Called on optimistic mutation.
 * @param {(delta: 1|-1) => void} props.onRollbackCountDelta - Called on rollback after failure.
 */
export default function MembersView({ department, onMemberCountDelta, onRollbackCountDelta }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingEmails, setPendingEmails] = useState(new Set());
  const [addEmail, setAddEmail] = useState("");
  const [addError, setAddError] = useState(null);
  const [addPending, setAddPending] = useState(false);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listDepartmentMembers(department.id);
      setMembers(extractMemberEmails(response.data));
    } catch (err) {
      setError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setLoading(false);
    }
  }, [department.id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const isValidEmail = (value) => EMAIL_REGEX.test(value);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = addEmail.trim();
    if (!trimmed || !isValidEmail(trimmed)) return;

    setAddError(null);
    setAddPending(true);

    // Optimistic add
    setMembers((prev) => [...prev, trimmed]);
    onMemberCountDelta(1);
    setAddEmail("");

    try {
      await assignUserToDepartment(trimmed, department.id);
    } catch (err) {
      // Rollback
      setMembers((prev) => prev.filter((m) => m !== trimmed));
      onRollbackCountDelta(-1);
      setAddError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setAddPending(false);
    }
  };

  const handleRemove = async (email) => {
    setAddError(null);

    // Mark as pending
    setPendingEmails((prev) => new Set([...prev, email]));

    // Optimistic remove
    setMembers((prev) => prev.filter((m) => m !== email));
    onMemberCountDelta(-1);

    try {
      await removeUserFromDepartment(email, department.id);
    } catch (err) {
      // Rollback
      setMembers((prev) => [...prev, email]);
      onRollbackCountDelta(1);
      setAddError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setPendingEmails((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
    }
  };

  const canSubmitAdd = addEmail.trim() && isValidEmail(addEmail.trim()) && !addPending;

  return (
    <tr className="members-view">
      <td colSpan={5}>
        <div className="members-view__panel">
          {loading && (
            <div className="members-view__loading">Loading members…</div>
          )}

          {!loading && error && (
            <div className="members-view__error" role="alert">
              {error}
              <button
                type="button"
                className="members-view__retry-btn"
                onClick={fetchMembers}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && members.length === 0 && (
            <div className="members-view__empty">
              This department has no members.
            </div>
          )}

          {!loading && !error && members.length > 0 && (
            <ul className="members-view__list">
              {members.map((email) => (
                <li key={email} className="members-view__item">
                  <span className="members-view__email">{email}</span>
                  <button
                    type="button"
                    className="members-view__remove-btn"
                    onClick={() => handleRemove(email)}
                    disabled={pendingEmails.has(email)}
                    aria-label={`Remove ${email}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <form className="members-view__add-form" onSubmit={handleAdd}>
            <label htmlFor={`add-member-${department.id}`} className="members-view__add-label">
              Add member
            </label>
            <input
              id={`add-member-${department.id}`}
              type="email"
              className="members-view__add-input"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              placeholder="user@example.com"
              disabled={addPending}
              aria-describedby={addError ? `add-member-error-${department.id}` : undefined}
            />
            <button
              type="submit"
              className="members-view__add-btn"
              disabled={!canSubmitAdd}
            >
              Add
            </button>
          </form>

          {addError && (
            <div
              id={`add-member-error-${department.id}`}
              className="members-view__error"
              role="alert"
            >
              {addError}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}
